import type { ReactNode } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { MousePointerClickIcon, SparklesIcon } from 'lucide-react';

import { REPORT_SELECTION_ACTIONS_ANCHOR } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import { SortableList } from '@modules/_shared/components/sortable-list/SortableList';
import type { ReorderEvent } from '@modules/_shared/components/sortable-list/types';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { useLocale } from '@shared/hooks/locale';

type ListingsSelectionSectionProps<T extends { id: string }> = {
  items: T[];
  loading: boolean;
  disabled?: boolean;
  automaticDisabled?: boolean;
  onAutomatic: () => void;
  onManual: () => void;
  onReorder: (event: ReorderEvent<T>) => void;
  renderItem: (props: { item: T; index: number }) => ReactNode;
  children?: ReactNode;
};

export const ListingsSelectionSection = <T extends { id: string }>({
  items,
  loading,
  disabled = false,
  automaticDisabled = false,
  onAutomatic,
  onManual,
  onReorder,
  renderItem,
  children,
}: ListingsSelectionSectionProps<T>) => {
  const { t } = useLocale();

  return (
    <Stack sx={{ gap: 1, p: 2, flexGrow: 1, height: 0, overflow: 'auto' }}>
      {children}
      <Stack
        {...tourAnchor(REPORT_SELECTION_ACTIONS_ANCHOR)}
        direction="row"
        sx={{ gap: 2, mt: 2 }}
      >
        <Button
          startIcon={<SparklesIcon />}
          fullWidth
          variant="contained"
          onClick={onAutomatic}
          loading={loading}
          loadingPosition="start"
          disabled={disabled || automaticDisabled}
        >
          {t('Automatically')}
        </Button>
        <Button
          startIcon={<MousePointerClickIcon />}
          onClick={onManual}
          variant="outlined"
          fullWidth
          disabled={loading || disabled}
        >
          {t('Manually')}
        </Button>
      </Stack>
      <Stack sx={{ gap: 2, mt: 1 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            {t('No properties selected')}
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {t('Selected properties')}
            </Typography>
            <SortableList
              items={items}
              onReorder={onReorder}
              disabled={loading || disabled}
              gap={2}
              renderItem={renderItem}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};
